/**
 * Firebase Analytics Hook
 *
 * Provides methods to track:
 * - Page views (automatic via React Router)
 * - Custom events (button clicks, form submissions)
 * - Conversions (subscription, signup)
 * - User properties (tier, platform)
 *
 * Uses Firebase Analytics for unified tracking across Web, iOS, Android
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getFirebaseAnalytics } from '../config/firebase';
import {
  logEvent,
  setUserProperties as setFirebaseUserProperties,
  setUserId,
} from 'firebase/analytics';

/**
 * Check if Firebase Analytics is available
 */
const isAnalyticsEnabled = (): boolean => {
  return getFirebaseAnalytics() !== null;
};

/**
 * Track page view
 * Call this when route changes
 */
export const trackPageView = (path: string, title?: string): void => {
  const analytics = getFirebaseAnalytics();
  if (!analytics) return;

  logEvent(analytics, 'page_view', {
    page_path: path,
    page_title: title,
  });
};

/**
 * Track custom event
 * @param eventName - Event name (e.g., 'button_click', 'form_submit')
 * @param params - Event parameters
 */
export const trackEvent = (
  eventName: string,
  params?: Record<string, unknown>
): void => {
  const analytics = getFirebaseAnalytics();
  if (!analytics) return;

  logEvent(analytics, eventName, params);
};

/**
 * Set user properties
 * Call after login/signup
 */
export const setUserProperties = (properties: {
  user_id?: string;
  tier?: 'free' | 'basic' | 'pro';
  platform?: string;
}): void => {
  const analytics = getFirebaseAnalytics();
  if (!analytics) return;

  // Set user ID separately
  if (properties.user_id) {
    setUserId(analytics, properties.user_id);
  }

  // Set other properties
  const { user_id, ...otherProps } = properties;
  if (Object.keys(otherProps).length > 0) {
    setFirebaseUserProperties(analytics, otherProps);
  }
};

// ============================================
// Pre-defined Event Trackers
// ============================================

/**
 * Track signup event
 */
export const trackSignup = (method: 'email' | 'google' | 'apple'): void => {
  trackEvent('sign_up', {
    method,
  });
};

/**
 * Track login event
 */
export const trackLogin = (method: 'email' | 'google' | 'apple'): void => {
  trackEvent('login', {
    method,
  });
};

/**
 * Track subscription purchase
 */
export const trackSubscription = (params: {
  tier: 'basic' | 'pro';
  interval: 'month' | 'year';
  value: number;
  currency: string;
}): void => {
  trackEvent('purchase', {
    transaction_id: `sub_${Date.now()}`,
    value: params.value,
    currency: params.currency,
    items: [
      {
        item_name: `${params.tier}_${params.interval}`,
        item_category: 'subscription',
        price: params.value,
      },
    ],
  });

  // Also track as conversion
  trackEvent('subscription_started', {
    tier: params.tier,
    interval: params.interval,
  });
};

/**
 * Track subscription cancellation
 */
export const trackSubscriptionCanceled = (tier: 'basic' | 'pro'): void => {
  trackEvent('subscription_canceled', {
    tier,
  });
};

/**
 * Track chat message sent
 */
export const trackMessageSent = (params: {
  model: string;
  hasAttachment: boolean;
  attachmentType?: 'image' | 'pdf' | 'audio' | 'video';
}): void => {
  trackEvent('message_sent', params);
};

/**
 * Track file upload
 */
export const trackFileUpload = (params: {
  fileType: string;
  fileSizeMB: number;
}): void => {
  trackEvent('file_upload', params);
};

/**
 * Track AI model selection
 */
export const trackModelSelected = (modelName: string, tier: string): void => {
  trackEvent('model_selected', {
    model_name: modelName,
    model_tier: tier,
  });
};

/**
 * Track pricing page view
 */
export const trackPricingViewed = (source: string): void => {
  trackEvent('pricing_viewed', {
    source, // e.g., 'navbar', 'upgrade_prompt', 'profile'
  });
};

/**
 * Track checkout started
 */
export const trackCheckoutStarted = (params: {
  tier: 'basic' | 'pro';
  interval: 'month' | 'year';
  value: number;
}): void => {
  trackEvent('begin_checkout', {
    value: params.value,
    currency: 'USD',
    items: [
      {
        item_name: `${params.tier}_${params.interval}`,
        item_category: 'subscription',
        price: params.value,
      },
    ],
  });
};

// ============================================
// React Hook
// ============================================

/**
 * Hook to automatically track page views on route changes
 * Add this to your App.tsx or main layout component
 */
export const usePageTracking = (): void => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
};

export default {
  isAnalyticsEnabled,
  trackPageView,
  trackEvent,
  setUserProperties,
  trackSignup,
  trackLogin,
  trackSubscription,
  trackSubscriptionCanceled,
  trackMessageSent,
  trackFileUpload,
  trackModelSelected,
  trackPricingViewed,
  trackCheckoutStarted,
  usePageTracking,
};
